import { forwardRef } from "react";
import { 
  Share2, 
  BadgeCheck, 
  AlertTriangle, 
  Clock,
  Users,
  MapPin,
  ChevronRight,
  Sparkles,
  PawPrint,
  Phone,
  Calendar
} from "lucide-react";
import shareCardBg from "@/assets/share-card-bg.png";

export const ShelterBrochure = forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <div
      ref={ref}
      style={{
        width: "800px",
        minHeight: "1100px",
        fontFamily: "'Nunito', sans-serif",
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header section with gradient */}
      <div
        style={{
          background: "linear-gradient(135deg, #5D9A7D 0%, #4E8A6E 100%)",
          padding: "36px 48px 40px",
          position: "relative",
        }}
      >
        {/* Logo and title */}
        <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "18px" }}>
          <img 
            src="/og-image.png" 
            alt="GdzieSchronisko logo"
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "28%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          />
          <div>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "white",
                margin: 0,
                letterSpacing: "-0.5px",
              }}
            >
              GdzieSchronisko
            </h1>
            <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.9)", margin: 0 }}>
              Mapa Schronisk dla Zwierząt w Polsce
            </p>
          </div>
        </div>

        {/* Main headline */}
        <h2
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "white",
            margin: 0,
            lineHeight: 1.3,
          }}
        >
          Dołącz do platformy łączącej schroniska<br />
          z ludźmi, którzy chcą pomagać
        </h2>

        {/* Subtitle */}
        <p
          style={{
            fontSize: "13px",
            color: "rgba(255,255,255,0.85)",
            margin: "10px 0 0",
            fontWeight: 500,
          }}
        >
          Serwis jest bezpłatny • Dane można aktualizować w dowolnym momencie
        </p>
      </div>

      {/* Problem/Solution section */}
      <div style={{ padding: "40px 48px", background: "#f8faf9" }}>
        <div style={{ display: "flex", gap: "24px", justifyContent: "center" }}>
          {/* Problem */}
          <div
            style={{
              flex: 1,
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#FEF3C7",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "20px" }}>😔</span>
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1f2937", margin: "0 0 8px" }}>
              Problem
            </h3>
            <p style={{ fontSize: "14px", color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
              Ludzie chcą pomagać schroniskom, ale nie wiedzą <strong>czego potrzebujecie</strong> i <strong>gdzie was szukać</strong>.
            </p>
          </div>

          {/* Solution */}
          <div
            style={{
              flex: 1,
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              border: "2px solid #5D9A7D",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#D1FAE5",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "20px" }}>✨</span>
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1f2937", margin: "0 0 8px" }}>
              Rozwiązanie
            </h3>
            <p style={{ fontSize: "14px", color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
              GdzieSchronisko.pl - miejsce gdzie <strong>Wasze aktualne potrzeby</strong> trafiają do osób gotowych pomóc.
            </p>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div style={{ padding: "40px 48px" }}>
        <h3
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: "#1f2937",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Sparkles style={{ width: "24px", height: "24px", color: "#5D9A7D" }} />
          Co oferuje platforma?
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {/* Feature 1 - Map */}
          <FeatureCard
            icon={<MapPin style={{ width: "20px", height: "20px", color: "#5D9A7D" }} />}
            title="Mapa interaktywna"
            description="Wasze schronisko widoczne na mapie Polski z pełnym profilem i danymi kontaktowymi."
          />

          {/* Feature 2 - Verification */}
          <FeatureCard
            icon={<BadgeCheck style={{ width: "20px", height: "20px", color: "#5D9A7D" }} />}
            title="Badge weryfikacji"
            description="Profil oznaczony jako 'Zweryfikowany przez organizację' buduje zaufanie darczyńców."
            highlight
          />

          {/* Feature 3 - Urgent needs */}
          <FeatureCard
            icon={<AlertTriangle style={{ width: "20px", height: "20px", color: "#f97316" }} />}
            title="Pilne potrzeby"
            description="Wyróżnij najpilniejsze potrzeby - pomarańczowy znacznik przyciąga uwagę."
            extraInfo="(status wygasa po 30 dniach)"
            urgent
            showUrgentBadge
          />

          {/* Feature 4 - Share graphics */}
          <FeatureCard
            icon={<Share2 style={{ width: "20px", height: "20px", color: "#5D9A7D" }} />}
            title="Generator grafik"
            description="Łatwo wygeneruj i edytuj grafikę z potrzebami do udostępnienia na social media."
          />

          {/* Feature 5 - Update date */}
          <FeatureCard
            icon={<Clock style={{ width: "20px", height: "20px", color: "#5D9A7D" }} />}
            title="Data aktualizacji"
            description="Widoczna data ostatniej aktualizacji pokazuje, że dane są aktualne."
          />

          {/* Feature 6 - Volunteers */}
          <FeatureCard
            icon={<Users style={{ width: "20px", height: "20px", color: "#5D9A7D" }} />}
            title="Wolontariat i spacery"
            description="Informuj o możliwości wolontariatu i spacerów z psami."
          />
        </div>

        {/* Why updates matter */}
        <div
          style={{
            marginTop: "24px",
            background: "linear-gradient(135deg, #E8F5EA 0%, #D4EED8 100%)",
            borderRadius: "12px",
            padding: "16px 20px",
            border: "1px solid #86efac",
          }}
        >
          <p style={{ fontSize: "14px", color: "#2D5A3D", margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
            💡 <strong>Regularne aktualizacje potrzeb mają ogromne znaczenie!</strong> Darczyńcy chętniej pomagają, gdy widzą aktualną datę - wiedzą wtedy, że potrzeby są rzeczywiste i pilne.
          </p>
          <p style={{ fontSize: "13px", color: "#3D6B54", margin: "10px 0 0", lineHeight: 1.5 }}>
            ⏱️ Aktualizacja to dosłownie 2 minuty - szybki formularz znajdziesz przy profilu schroniska.
          </p>
        </div>
      </div>

      {/* Example graphic section - styled like actual MultiShelterShareCard */}
      <div style={{ padding: "0 48px 40px" }}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#1f2937",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Share2 style={{ width: "20px", height: "20px", color: "#5D9A7D" }} />
          Przykład grafiki do udostępniania
        </h3>

        {/* Mini example styled like actual share card */}
        <div
          style={{
            backgroundImage: `url(${shareCardBg})`,
            backgroundSize: "cover",
            borderRadius: "16px",
            padding: "24px",
            border: "1px solid #e5e7eb",
          }}
        >
          {/* Brand badge at top - green rounded square with white paw */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                background: "#4E8A6E",
                borderRadius: 8,
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PawPrint style={{ width: 16, height: 16, color: "#FFFFFF" }} />
            </div>
            <span style={{ color: "#2D5A3D", fontSize: "14px", fontWeight: 700 }}>
              GdzieSchronisko
            </span>
          </div>

          <h4 style={{ fontSize: "22px", fontWeight: 800, color: "#2D5A3D", margin: "0 0 10px", textAlign: "center" }}>
            Jak możesz pomóc?
          </h4>

          {/* Shelter card */}
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              position: "relative",
            }}
          >
            {/* PILNE badge - flat red style */}
            <div
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "#EF4444",
                padding: "4px 10px",
                borderRadius: 6,
                boxShadow: "0 2px 6px rgba(239, 68, 68, 0.3)",
              }}
            >
              <AlertTriangle style={{ width: 12, height: 12, color: "#FFFFFF" }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Pilne!
              </span>
            </div>

            <h5 style={{ fontSize: "16px", fontWeight: 800, color: "#2D5A3D", margin: "0 0 8px", textTransform: "uppercase" }}>
              Przykładowe Schronisko
            </h5>

            {/* Address and phone */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", fontSize: "12px", color: "#6B7280" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#2D5A3D" }}>
                <MapPin style={{ width: 12, height: 12 }} />
                <span style={{ fontWeight: 500 }}>ul. Przykładowa 123, Warszawa</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Phone style={{ width: 12, height: 12 }} />
                <span>22 123 45 67</span>
              </div>
            </div>

            {/* Potrzebne teraz badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "linear-gradient(135deg, #E8F5EA 0%, #D4EED8 100%)",
                padding: "4px 10px",
                borderRadius: 6,
                marginBottom: "10px",
              }}
            >
              <PawPrint style={{ width: 12, height: 12, color: "#2D5A3D" }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#2D5A3D", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Potrzebne teraz:
              </span>
            </div>

            <div style={{ fontSize: "13px", color: "#374151", lineHeight: 1.5 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", marginBottom: "4px" }}>
                <span style={{ color: "#2D5A3D", fontWeight: 700 }}>●</span>
                <span>karma sucha i mokra dla psów i kotów</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", marginBottom: "4px" }}>
                <span style={{ color: "#2D5A3D", fontWeight: 700 }}>●</span>
                <span>koce, ręczniki, środki czystości</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                <span style={{ color: "#2D5A3D", fontWeight: 700 }}>●</span>
                <span>smycze, obroże, zabawki</span>
              </div>
            </div>

            {/* Update date */}
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "12px", fontSize: "11px", color: "#9CA3AF" }}>
              <Calendar style={{ width: 11, height: 11 }} />
              <span>Aktualizacja: 28 stycznia 2026</span>
            </div>
          </div>

          {/* Footer - compact green bar */}
          <div
            style={{
              marginTop: "14px",
              background: "#4E8A6E",
              borderRadius: "8px",
              padding: "6px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
            <span style={{ fontSize: "11px", color: "white", fontWeight: 700 }}>
              GdzieSchronisko.pl
            </span>
            <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
              - mapa schronisk i ich aktualnych potrzeb
            </span>
          </div>
          
          {/* Disclaimer */}
          <p style={{ fontSize: "9px", color: "#6B8A7A", textAlign: "center", margin: "6px 0 0", fontWeight: 500 }}>
            Przed pomocą skontaktuj się z organizacją
          </p>
        </div>
      </div>

      {/* CTA section */}
      <div
        style={{
          background: "linear-gradient(135deg, #5D9A7D 0%, #4E8A6E 100%)",
          padding: "32px 48px",
          margin: "0 48px 40px",
          borderRadius: "20px",
        }}
      >
        <h3
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: "white",
            margin: "0 0 16px",
            textAlign: "center",
          }}
        >
          Jak dołączyć?
        </h3>
        
        <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
          <div style={{ width: "180px" }}>
            <StepBadge number={1} text="Wejdź na GdzieSchronisko.pl" />
          </div>
          <ChevronRight style={{ width: "24px", height: "24px", color: "rgba(255,255,255,0.6)", marginTop: "6px" }} />
          <div style={{ width: "180px" }}>
            <StepBadge number={2} text="Kliknij 'Dodaj schronisko'" />
          </div>
          <ChevronRight style={{ width: "24px", height: "24px", color: "rgba(255,255,255,0.6)", marginTop: "6px" }} />
          <div style={{ width: "180px" }}>
            <StepBadge number={3} text="Wypełnij formularz" />
          </div>
        </div>

        <p
          style={{
            fontSize: "14px",
            color: "rgba(255,255,255,0.9)",
            textAlign: "center",
            margin: "20px 0 0",
          }}
        >
          Możesz też zaktualizować istniejący profil - zaznacz opcję<br />
          <strong>"Reprezentuję tę organizację"</strong> aby uzyskać badge weryfikacji.
        </p>

        {/* One-time update note */}
        <div
          style={{
            marginTop: "16px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.15)",
              borderRadius: "10px",
              padding: "12px 24px",
              textAlign: "center",
              maxWidth: "380px",
            }}
          >
            <p style={{ fontSize: "13px", color: "white", margin: 0, fontWeight: 500 }}>
              💡 Nawet <strong>jednorazowe dodanie informacji</strong> jest bardzo pomocne - dzięki temu Wasze schronisko będzie widoczne na mapie!
            </p>
          </div>
        </div>
      </div>

      {/* Footer with contact */}
      <div
        style={{
          padding: "24px 48px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img 
              src="/og-image.png" 
              alt="GdzieSchronisko logo"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "28%",
              }}
            />
            <div>
              <p style={{ fontSize: "14px", fontWeight: 700, color: "#2D5A3D", margin: 0 }}>
                GdzieSchronisko
              </p>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                Razem dla zwierząt
              </p>
            </div>
          </div>
        </div>

        {/* Contact info */}
        <div
          style={{
            background: "#f8faf9",
            borderRadius: "10px",
            padding: "12px 16px",
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: "13px", color: "#4b5563", margin: 0 }}>
            Masz pytania? Napisz do nas: <strong style={{ color: "#2D5A3D" }}>kontakt@gdzieschronisko.pl</strong>
          </p>
        </div>
      </div>
    </div>
  );
});

ShelterBrochure.displayName = "ShelterBrochure";

// Helper components
const FeatureCard = ({
  icon,
  title,
  description,
  highlight,
  urgent,
  extraInfo,
  showUrgentBadge,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight?: boolean;
  urgent?: boolean;
  extraInfo?: string;
  showUrgentBadge?: boolean;
}) => (
  <div
    style={{
      background: highlight ? "#f0fdf4" : urgent ? "#fff7ed" : "#f9fafb",
      borderRadius: "12px",
      padding: "16px",
      border: highlight ? "1px solid #86efac" : urgent ? "1px solid #fed7aa" : "1px solid #e5e7eb",
      position: "relative",
    }}
  >
    {showUrgentBadge && (
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          display: "flex",
          alignItems: "center",
          gap: 4,
          background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
          padding: "3px 8px",
          borderRadius: 5,
        }}
      >
        <AlertTriangle style={{ width: 10, height: 10, color: "#FFFFFF" }} />
        <span style={{ fontSize: 9, fontWeight: 700, color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Pilne!
        </span>
      </div>
    )}
    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
      <div
        style={{
          width: "36px",
          height: "36px",
          background: "white",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        {icon}
      </div>
      <div>
        <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1f2937", margin: "0 0 4px" }}>
          {title}
        </h4>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
          {description}
          {extraInfo && (
            <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "4px" }}>
              {extraInfo}
            </span>
          )}
        </p>
      </div>
    </div>
  </div>
);

const StepBadge = ({ number, text }: { number: number; text: string }) => (
  <div style={{ textAlign: "center" }}>
    <div
      style={{
        width: "36px",
        height: "36px",
        background: "white",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 8px",
        fontWeight: 700,
        color: "#5D9A7D",
        fontSize: "16px",
      }}
    >
      {number}
    </div>
    <p style={{ fontSize: "13px", color: "white", margin: 0, fontWeight: 500 }}>{text}</p>
  </div>
);
